// web-frontend/src/shared/constants/index.js
// 🔥 웹용 constants 통합 export
// ====================================

// ====================================
// 🎨 디자인 시스템 - Colors
// ====================================
export {
  Colors,
  DarkColors,
  ColorUtils,
} from './colors';

// ====================================
// 📏 디자인 시스템 - Dimensions
// ====================================
export {
  Spacing,
  BorderRadius,
  FontSizes,
  FontWeights,
  IconSizes,
  Screen,
  Layout,
  Shadows,
  ZIndex,
} from './dimensions';

// ====================================
// 🎨 디자인 시스템 - Theme
// ====================================
export {
  LightTheme,
  DarkTheme,
  Theme,
  getTheme,
  createWebStyles,
} from './theme';

// ====================================
// 🔧 디자인 시스템 - Design
// ====================================
export { default as Design } from './Design';

// ====================================
// 🌍 다국어 & 테마 설정 (appStore용)
// ====================================

/**
 * 지원 언어 목록
 * @type {Object}
 */
export const LANGUAGES = {
  KO: 'ko', // 한국어
  EN: 'en', // English
  JA: 'ja', // 日本語
  ZH: 'zh', // 中文
  ES: 'es', // Español
  FR: 'fr', // Français
  DE: 'de', // Deutsch
  RU: 'ru', // Русский
  AR: 'ar', // العربية
  PT: 'pt', // Português
};

/**
 * 언어 표시 이름 매핑
 * @type {Object}
 */
export const LANGUAGE_NAMES = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '中文',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  ru: 'Русский',
  ar: 'العربية',
  pt: 'Português',
};

/**
 * 테마 설정
 * @type {Object}
 */
export const THEMES = {
  LIGHT: 'light',   // 라이트 모드
  DARK: 'dark',     // 다크 모드
  SYSTEM: 'system', // 시스템 설정 따르기
};

// ====================================
// ⚙️ 앱 설정
// ====================================

/**
 * 애플리케이션 전역 설정
 * @type {Object}
 */
export const APP_CONFIG = {
  name: 'DSK',
  version: '1.0.0',
  environment: import.meta.env.MODE || 'development',
  apiUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
  timeout: 30000,
  retryCount: 3,
};

// ====================================
// 🔍 정렬 & 필터 옵션
// ====================================

/**
 * 정렬 옵션 목록
 * @type {Array}
 */
export const SORT_OPTIONS = [
  { id: 'name_asc', name: '이름순 (ㄱ-ㅎ)' },
  { id: 'name_desc', name: '이름순 (ㅎ-ㄱ)' },
  { id: 'booth_asc', name: '부스번호순 (낮은순)' },
  { id: 'booth_desc', name: '부스번호순 (높은순)' },
  { id: 'recent', name: '최근 등록순' },
];

/**
 * 필터 타입
 * @type {Object}
 */
export const FILTER_TYPES = {
  ALL: 'all',
  BOOKMARKED: 'bookmarked',
  CATEGORY: 'category',
  SEARCH: 'search',
};

// ====================================
// 📱 반응형 디자인 - 브레이크포인트
// ====================================

/**
 * 화면 크기 브레이크포인트 (px)
 * @type {Object}
 */
export const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
};

// ====================================
// ⏱️ 애니메이션 설정
// ====================================

/**
 * 애니메이션 지속시간 (ms)
 * @type {Object}
 */
export const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
};

// ====================================
// 📄 페이지네이션 설정
// ====================================

/**
 * 페이지네이션 설정
 * @type {Object}
 */
export const PAGINATION = {
  itemsPerPage: 20,
  maxPages: 10,
};

// ====================================
// 🎨 상태별 색상
// ====================================

/**
 * 상태 표시 색상
 * @type {Object}
 */
export const StatusColors = {
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#F44336',
  info: '#2196F3',
};

/**
 * 그라데이션 색상 조합
 * @type {Object}
 */
export const GradientColors = {
  primary: ['#2E7D32', '#4CAF50'],
  secondary: ['#4CAF50', '#81C784'],
  sunset: ['#FF9800', '#F44336'],
  ocean: ['#2196F3', '#03A9F4'],
};

/**
 * 투명도 레벨
 * @type {Object}
 */
export const OpacityLevels = {
  transparent: 0,
  light: 0.1,
  medium: 0.5,
  heavy: 0.8,
  opaque: 1,
};

// ====================================
// 👥 사용자 & 권한
// ====================================

/**
 * 사용자 역할
 * @type {Object}
 */
export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  GUEST: 'guest',
};

// ====================================
// 💬 채팅 & 메시징
// ====================================

/**
 * 채팅방 타입
 * @type {Object}
 */
export const CHAT_TYPES = {
  DIRECT: 'direct',
  GROUP: 'group',
  CHANNEL: 'channel',
};

/**
 * 메시지 타입
 * @type {Object}
 */
export const MESSAGE_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  FILE: 'file',
  SYSTEM: 'system',
};

/**
 * 채팅 메시지 타입 (확장 버전)
 * @type {Object}
 */
export const CHAT_MESSAGE_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  FILE: 'file',
  SYSTEM: 'system',
  AUDIO: 'audio',
  VIDEO: 'video',
};

// ====================================
// 📅 미팅 관리
// ====================================

/**
 * 미팅 상태
 * @type {Object}
 */
export const MEETING_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
};

// ====================================
// 🔔 알림
// ====================================

/**
 * 알림 타입
 * @type {Object}
 */
export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
};

// ====================================
// 🌱 탄소 중립 활동
// ====================================

/**
 * 탄소 활동 타입
 * @type {Object}
 */
export const CARBON_ACTIVITY_TYPES = {
  TRANSPORT: 'transport',
  EVENT: 'event',
  DONATION: 'donation',
};

// ====================================
// 📝 문자열 리소스
// ====================================

/**
 * 앱 내 사용되는 공통 문자열
 * @type {Object}
 */
export const Strings = {
  app: {
    name: 'DSK',
    version: '1.0.0',
  },
  common: {
    save: '저장',
    cancel: '취소',
    delete: '삭제',
    edit: '편집',
    confirm: '확인',
  },
};

// ====================================
// 🌐 API 설정
// ====================================

/**
 * API 통신 설정
 * @type {Object}
 */
export const API_CONFIG = {
  timeout: 30000,
  retryCount: 3,
};

// ====================================
// 🔥 주의사항
// ====================================
// - Default export를 사용하지 않습니다
// - Named exports만 사용하여 명시적인 import를 권장합니다
// - 예: import { LANGUAGES, THEMES } from '@/shared/constants'